'use client';

import { useState } from 'react';
import type { DiagnosticResults } from './ResultsInteractive';

interface Recommendation {
  id: string;
  priority: 1 | 2 | 3;
  area: string;
  title: string;
  description: string;
  impact: string;
  timeframe: string;
  impactLevel: 'Alto' | 'Médio' | 'Elevado';
}

const RECOMMENDATIONS_BY_PROFILE: Record<DiagnosticResults['profile'], Recommendation[]> = {
  stable: [
    {
      id: 'r1',
      priority: 1,
      area: 'Organização Administrativa',
      title: 'Implementar revisão periódica de processos',
      description: 'Estabelecer um calendário semestral de auditoria interna para identificar oportunidades de melhoria contínua nos processos administrativos.',
      impact: 'Manutenção da eficiência operacional e prevenção de degradação dos processos.',
      timeframe: '3-6 meses',
      impactLevel: 'Médio',
    },
    {
      id: 'r2',
      priority: 2,
      area: 'Gestão Financeira',
      title: 'Otimizar relatórios de execução orçamental',
      description: 'Automatizar a geração de relatórios financeiros mensais para reduzir o tempo de preparação e aumentar a precisão dos dados.',
      impact: 'Redução de 40% no tempo dedicado a relatórios financeiros.',
      timeframe: '1-3 meses',
      impactLevel: 'Médio',
    },
    {
      id: 'r3',
      priority: 3,
      area: 'Recursos Humanos',
      title: 'Desenvolver plano de formação contínua',
      description: 'Criar um programa estruturado de desenvolvimento profissional para a equipa, focado em competências digitais e legislação autárquica.',
      impact: 'Aumento da capacidade técnica da equipa e redução de erros processuais.',
      timeframe: '6-12 meses',
      impactLevel: 'Médio',
    },
  ],
  punctual: [
    {
      id: 'r1',
      priority: 1,
      area: 'Contratação Pública',
      title: 'Apoio técnico em procedimentos de contratação',
      description: 'Implementar suporte especializado para os procedimentos de contratação pública, garantindo conformidade com o CCP e reduzindo o risco de impugnações.',
      impact: 'Eliminação de riscos legais e aceleração dos processos de aquisição.',
      timeframe: '1-2 meses',
      impactLevel: 'Alto',
    },
    {
      id: 'r2',
      priority: 2,
      area: 'Gestão Financeira',
      title: 'Revisão do sistema de controlo interno',
      description: 'Avaliar e reforçar os mecanismos de controlo interno financeiro, incluindo segregação de funções e procedimentos de autorização.',
      impact: 'Redução de irregularidades e melhoria na preparação para auditorias externas.',
      timeframe: '2-4 meses',
      impactLevel: 'Alto',
    },
    {
      id: 'r3',
      priority: 3,
      area: 'Organização Administrativa',
      title: 'Digitalização e arquivo documental',
      description: 'Implementar um sistema de gestão documental digital para organizar e preservar os arquivos administrativos da junta.',
      impact: 'Redução de 60% no tempo de pesquisa documental e conformidade com legislação de arquivo.',
      timeframe: '3-6 meses',
      impactLevel: 'Médio',
    },
  ],
  support: [
    {
      id: 'r1',
      priority: 1,
      area: 'Gestão Financeira',
      title: 'Implementação urgente de suporte financeiro especializado',
      description: 'Contratar apoio técnico especializado em gestão financeira autárquica para regularizar processos, garantir conformidade com o SNC-AP e preparar relatórios de prestação de contas.',
      impact: 'Prevenção de irregularidades financeiras e conformidade com obrigações legais de reporte.',
      timeframe: 'Imediato (1 mês)',
      impactLevel: 'Elevado',
    },
    {
      id: 'r2',
      priority: 2,
      area: 'Contratação Pública',
      title: 'Regularização de procedimentos de contratação',
      description: 'Auditoria completa aos procedimentos de contratação pública em curso e implementação de templates e checklists conformes com o CCP.',
      impact: 'Eliminação de riscos de impugnação e aceleração dos processos de aquisição de bens e serviços.',
      timeframe: '1-3 meses',
      impactLevel: 'Elevado',
    },
    {
      id: 'r3',
      priority: 3,
      area: 'Recursos Humanos',
      title: 'Reestruturação da gestão de recursos humanos',
      description: 'Revisão dos processos de gestão de pessoal, incluindo avaliação de desempenho, processamento de vencimentos e conformidade com o SIADAP.',
      impact: 'Redução de erros no processamento salarial e conformidade com legislação laboral do setor público.',
      timeframe: '2-4 meses',
      impactLevel: 'Alto',
    },
  ],
  pressure: [
    {
      id: 'r1',
      priority: 1,
      area: 'Intervenção Global',
      title: 'Diagnóstico aprofundado e plano de recuperação operacional',
      description: 'Iniciar imediatamente um processo de diagnóstico técnico aprofundado com equipa especializada GestPub para mapear todas as áreas críticas e definir um plano de recuperação operacional prioritizado.',
      impact: 'Estabilização operacional urgente e prevenção de consequências legais e financeiras graves.',
      timeframe: 'Urgente (2 semanas)',
      impactLevel: 'Elevado',
    },
    {
      id: 'r2',
      priority: 2,
      area: 'Gestão Financeira e Contratação',
      title: 'Suporte técnico contínuo em áreas críticas',
      description: 'Implementar um regime de apoio técnico mensal cobrindo gestão financeira, contratação pública e conformidade legal, com disponibilidade para resposta rápida a situações urgentes.',
      impact: 'Redução imediata da pressão operacional e garantia de conformidade nas áreas de maior risco.',
      timeframe: 'Imediato',
      impactLevel: 'Elevado',
    },
    {
      id: 'r3',
      priority: 3,
      area: 'Capacitação da Equipa',
      title: 'Programa intensivo de capacitação técnica',
      description: 'Desenvolver um programa de formação intensiva para a equipa nas áreas identificadas como críticas, com acompanhamento prático e materiais de referência adaptados à realidade da junta.',
      impact: 'Aumento da autonomia técnica da equipa e redução da dependência de suporte externo a médio prazo.',
      timeframe: '1-3 meses',
      impactLevel: 'Alto',
    },
  ],
};

interface RecommendationsPanelProps {
  results: DiagnosticResults;
}

const impactStyles: Record<string, { backgroundColor: string; color: string }> = {
  'Elevado': { backgroundColor: 'rgba(198, 40, 40, 0.08)', color: '#C62828' },
  'Alto': { backgroundColor: 'rgba(230, 81, 0, 0.08)', color: '#E65100' },
  'Médio': { backgroundColor: 'rgba(245, 124, 0, 0.08)', color: '#F57C00' },
};

export default function RecommendationsPanel({ results }: RecommendationsPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>('r1');
  const recommendations = RECOMMENDATIONS_BY_PROFILE[results.profile];

  return (
    <div
      className="bg-white border border-gray-100 p-7"
      style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
    >
      <div className="flex items-center gap-2 mb-5">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <h3
          className="text-gray-900 font-semibold"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.15rem', fontWeight: 700 }}
        >
          Recomendações Prioritárias
        </h3>
      </div>

      <p
        className="text-gray-500 text-sm mb-5 leading-relaxed"
        style={{ fontFamily: 'DM Sans, sans-serif' }}
      >
        Com base no seu diagnóstico, identificamos{' '}
        <strong className="text-gray-900 font-semibold">3 áreas prioritárias</strong>{' '}
        de intervenção para a sua junta de freguesia.
      </p>

      <div className="flex flex-col gap-2">
        {recommendations.map((rec) => {
          const isExpanded = expandedId === rec.id;

          return (
            <div
              key={rec.id}
              className="overflow-hidden transition-all duration-250"
              style={{
                borderRadius: '5px',
                border: isExpanded ? '1px solid #009688' : '1px solid #F0F0F0',
              }}
            >
              <button
                type="button"
                onClick={() => setExpandedId(isExpanded ? null : rec.id)}
                className="w-full flex items-center gap-3 p-4 text-left transition-colors duration-150 hover:bg-gray-50"
                aria-expanded={isExpanded}
              >
                <div
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center font-bold text-sm text-white"
                  style={{
                    borderRadius: '5px',
                    fontFamily: 'JetBrains Mono, monospace',
                    fontSize: '0.75rem',
                    backgroundColor: rec.priority === 1 ? '#009688' : rec.priority === 2 ? '#00796B' : '#B2DFDB',
                    color: rec.priority === 3 ? '#009688' : 'white',
                  }}
                >
                  {rec.priority}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-0.5">
                    <span className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>
                      {rec.area}
                    </span>
                    <span
                      className="text-xs px-2 py-0.5 font-semibold"
                      style={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '5px', ...impactStyles[rec.impactLevel] }}
                    >
                      Impacto {rec.impactLevel}
                    </span>
                  </div>
                  <p
                    className="font-semibold text-sm text-gray-900 leading-tight"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    {rec.title}
                  </p>
                </div>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#888"
                  strokeWidth="2"
                  className={`flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              {isExpanded && (
                <div
                  className="px-4 pb-5"
                  style={{ borderTop: '1px solid #F0F0F0', backgroundColor: '#FAFAFA' }}
                >
                  <p
                    className="text-sm text-gray-700 leading-relaxed mt-4 mb-4"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    {rec.description}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div
                      className="bg-white border border-gray-100 p-3.5"
                      style={{ borderRadius: '5px' }}
                    >
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2" aria-hidden="true">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                          <polyline points="17 6 23 6 23 12"/>
                        </svg>
                        <span
                          className="text-xs font-semibold text-gray-400 uppercase tracking-wide"
                          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.65rem', letterSpacing: '0.08em' }}
                        >
                          Impacto Esperado
                        </span>
                      </div>
                      <p
                        className="text-xs text-gray-700 leading-relaxed"
                        style={{ fontFamily: 'DM Sans, sans-serif' }}
                      >
                        {rec.impact}
                      </p>
                    </div>
                    <div
                      className="bg-white border border-gray-100 p-3.5"
                      style={{ borderRadius: '5px' }}
                    >
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        <span
                          className="text-xs font-semibold text-gray-400 uppercase tracking-wide"
                          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.65rem', letterSpacing: '0.08em' }}
                        >
                          Prazo Estimado
                        </span>
                      </div>
                      <p
                        className="text-xs text-gray-700 font-semibold"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {rec.timeframe}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}